import * as THREE from "three"
import { deriveSkyFamily, PALETTE, type SkyFamily } from "@/lib/location-scene-3d/light-palette"

/** Keep in sync with env chrome `background` — horizon base, family from light-palette. */
export type SkyDomeEnvironment = {
  background: string
  sky_markup: "dots" | "plus" | "none"
}

/** Port of the light-palette module's sky shader (Светлый). Do not reinvent the grid math. */
const SKY_VERTEX = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  vec4 p = projectionMatrix * mat4(mat3(viewMatrix)) * vec4(position, 1.0);
  gl_Position = vec4(p.xy, p.w * 0.99999, p.w);
}
`

const SKY_FRAGMENT = /* glsl */ `
varying vec3 vDir;
uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uGround;
uniform vec3 uMark;
uniform vec3 uMarkMaj;
uniform float uStep;
uniform float uDpr;
uniform float uMarkupMode; // -1 = none, 0 = dots, 1 = plus
const float PI = 3.14159265;

float crossMask(vec2 p, float arm, float th) {
  float h = (1.0 - smoothstep(th - 0.5, th + 0.5, abs(p.y))) * (1.0 - smoothstep(arm - 0.5, arm + 0.5, abs(p.x)));
  float v = (1.0 - smoothstep(th - 0.5, th + 0.5, abs(p.x))) * (1.0 - smoothstep(arm - 0.5, arm + 0.5, abs(p.y)));
  return max(h, v);
}

float dotMask(vec2 p, float radius) {
  return 1.0 - smoothstep(radius - 0.5, radius + 0.5, length(p));
}

void main() {
  vec3 d = normalize(vDir);
  float angPerPx = length(fwidth(d));
  float lat = asin(clamp(d.y, -1.0, 1.0));
  float lon = atan(d.z, d.x);

  float row = floor(lat / uStep + 0.5);
  float rowLat = row * uStep;
  float n = max(1.0, floor(2.0 * PI * cos(rowLat) / uStep + 0.5));
  float cell = 2.0 * PI / n;
  float shift = mod(row, 2.0) * 0.5;
  float k = floor(lon / cell - shift + 0.5);
  float nodeLon = (k + shift) * cell;
  vec2 px = vec2((lon - nodeLon) * cos(lat), lat - rowLat) / max(angPerPx, 1e-6);

  bool major = mod(row, 3.0) == 0.0 && mod(k, 3.0) == 0.0;
  float m = 0.0;
  if (uMarkupMode > -0.5) {
    if (uMarkupMode > 0.5) {
      // Thin hairline marks, not bold crosshatch.
      m = crossMask(px, (major ? 2.6 : 1.6) * uDpr, 0.2 * uDpr);
    } else {
      m = dotMask(px, (major ? 1.0 : 0.65) * uDpr);
    }
    m *= 1.0 - smoothstep(radians(64.0), radians(78.0), abs(lat));
  }

  float up = clamp(d.y, 0.0, 1.0);
  float dn = clamp(-d.y, 0.0, 1.0);
  vec3 col = d.y >= 0.0
    ? mix(uHorizon, uZenith, pow(up, 0.7))
    : mix(uHorizon, uGround, pow(dn, 0.5));
  col = mix(col, major ? uMarkMaj : uMark, m);

  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}
`

export type SkyDome = {
  mesh: THREE.Mesh
  applyEnvironment: (env: SkyDomeEnvironment) => void
  /** Hemi ground / sky below horizon color for the current environment (unscaled by ambient factor). */
  groundHex: () => string
  /** Dim the sky's own colors in step with the lamp-driven studio rig (see engine.ts). 1 = normal. */
  setAmbientFactor: (factor: number) => void
  setPixelRatio: (dpr: number) => void
  syncToCamera: (camera: THREE.Camera) => void
  dispose: () => void
}

const DEFAULT_SKY_FAMILY: SkyFamily = {
  horizon: PALETTE.horizon,
  zenith: PALETTE.zenith,
  ground: PALETTE.ground,
  mark: PALETTE.mark,
  markMaj: PALETTE.markMaj,
}

export function createSkyDome(scene: THREE.Scene): SkyDome {
  let family: SkyFamily = DEFAULT_SKY_FAMILY
  let ambientFactor = 1

  const uniforms = {
    uZenith: { value: new THREE.Color(PALETTE.zenith) },
    uHorizon: { value: new THREE.Color(PALETTE.horizon) },
    uGround: { value: new THREE.Color(PALETTE.ground) },
    uMark: { value: new THREE.Color(PALETTE.mark) },
    uMarkMaj: { value: new THREE.Color(PALETTE.markMaj) },
    uStep: { value: THREE.MathUtils.degToRad(10) },
    uDpr: { value: Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2) },
    uMarkupMode: { value: 1 },
  }

  // The dome is unlit (toneMapped: false) — it must re-darken itself when the studio rig dims,
  // or the "room" stays palette-bright no matter how low the lamp slider goes.
  const refreshColors = () => {
    uniforms.uHorizon.value.set(family.horizon).multiplyScalar(ambientFactor)
    uniforms.uZenith.value.set(family.zenith).multiplyScalar(ambientFactor)
    uniforms.uGround.value.set(family.ground).multiplyScalar(ambientFactor)
    uniforms.uMark.value.set(family.mark).multiplyScalar(ambientFactor)
    uniforms.uMarkMaj.value.set(family.markMaj).multiplyScalar(ambientFactor)
  }

  const material = new THREE.ShaderMaterial({
    name: "LocationSceneSkyDome",
    uniforms,
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: true,
    fog: false,
    toneMapped: false,
  })

  const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 64, 32), material)
  mesh.renderOrder = -1
  mesh.frustumCulled = false
  mesh.raycast = () => {}
  mesh.userData = { kind: "skyDome", ignorePick: true }
  // Theme sky is a screen gradient plus world crosses. This dome must not cover them.
  mesh.visible = false
  scene.add(mesh)

  const applyEnvironment = (env: SkyDomeEnvironment) => {
    family = deriveSkyFamily(env.background)
    uniforms.uMarkupMode.value = -1
    refreshColors()
  }

  const setAmbientFactor = (factor: number) => {
    ambientFactor = factor
    refreshColors()
  }

  const setPixelRatio = (dpr: number) => {
    uniforms.uDpr.value = Math.min(Math.max(dpr, 0.5), 2)
  }

  const syncToCamera = (camera: THREE.Camera) => {
    mesh.position.copy(camera.position)
  }

  const dispose = () => {
    scene.remove(mesh)
    mesh.geometry.dispose()
    material.dispose()
  }

  return {
    mesh,
    applyEnvironment,
    groundHex: () => family.ground,
    setAmbientFactor,
    setPixelRatio,
    syncToCamera,
    dispose,
  }
}
